import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Icon } from '@iconify/react';
import {
  Button,
  Container,
  Box,
  Alert,
  Modal,
  Typography,
  Snackbar,
  TextField,
  Paper
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import CustomLoader from '../components/Loader/CustomLoader';
import Header from '../components/Headers/Header';
import NestPassModel from '../components/NestPass/NestPassModel';
import { GetStore, UpdateNestPassNotificationCronTimeSlot } from '../apollo';
import useGlobalStyles from '../utils/globalStyles'; // Import global styles
import TableHeader from '../components/TableHeader'; // Import TableHeader
import { customStyles } from '../utils/tableStyleForSore';

// GraphQL query
const GET_NEST_PASS_DETAILS = gql`${GetStore}`;
const UPDATE_NEST_PASS_NOTIFICATION_CRON_TIME_SLOT = gql`${UpdateNestPassNotificationCronTimeSlot}`;

const ManageNestPass = (props) => {
  const { t } = props;
  const globalClasses = useGlobalStyles(); // Use global styles
  const userType = JSON.parse(localStorage.getItem('user-native'));
  const storeId = userType && userType.storeId;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedNestPass, setSelectedNestPass] = useState(null);
  // const [openSnackbar, setOpenSnackbar] = useState(false); // Removed redundant state
  // const [success, setSuccess] = useState(''); // Removed redundant state
  const [queryError, setQueryError] = useState(''); // To avoid naming conflict with error from useQuery
  // const [anchorEl, setAnchorEl] = useState(null); // For the action menu
  // const isMenuOpen = Boolean(anchorEl);
  // const [selectedRow, setSelectedRow] = useState(null); // To avoid naming conflict with error from useQuery
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [cronStartTime, setCronStartTime] = useState('06:00');
  const [cronEndTime, setCronEndTime] = useState('06:00');
  const { loading, error, data, refetch } = useQuery(GET_NEST_PASS_DETAILS, {
    variables: { storeId },
  });
  const [updateCronTimeSlot, { loading: updatingCron, error: updateCronError }] = useMutation(
    UPDATE_NEST_PASS_NOTIFICATION_CRON_TIME_SLOT,
    {
      onCompleted: () => {
        setSnackbarMessage(t('Cron time slot updated successfully'));
        setSnackbarOpen(true);
        setSnackbarSeverity('success');
        refetch(); // Refetch store data to update the displayed cron times
      },
      onError: (err) => {
        setSnackbarMessage(t('Error updating cron time slot') + ': ' + err.message);
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
      },
    }
  );
  useEffect(() => {
    if (data && data.getStore && data.getStore.nestPassExpiryNotificationSlot) {
      setCronStartTime(data.getStore.nestPassExpiryNotificationSlot.start || '06:00');
      setCronEndTime(data.getStore.nestPassExpiryNotificationSlot.end || '06:00');
    }
  }, [data]);

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setSelectedNestPass(null);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleOpenEditModal = (nestPass) => {
    setSelectedNestPass(nestPass);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedNestPass(null);
    setEditModalOpen(false);
  };
  const handleCronStartTimeChange = (event) => {
    setCronStartTime(event.target.value);
  };

  const handleCronEndTimeChange = (event) => {
    setCronEndTime(event.target.value);
  };

  const handleUpdateCronTimeSlot = () => {
    updateCronTimeSlot({ variables: { start: cronStartTime, end: cronEndTime } });
  };
  const columns = [
    {
      name: t('Code'),
      selector: (row) => row && row.nestPass && row.nestPass.code,
      sortable: true,
    },
    {
      name: t('Regular Price'),
      selector: (row) => row && row.nestPass && row.nestPass.regularPurchasePrice,
      sortable: true,
    },
    {
      name: t('Discounted Price'),
      selector: (row) => row && row.nestPass && row.nestPass.discountedPurchasePrice,
      sortable: true,
    },
    /* {
      name: t('HSN'),
      selector: row => row && row.nestPass && row.nestPass.hsn,
      sortable: true,
    },
    {
      name: t('SGST (%)'),
      selector: row => row && row.nestPass && row.nestPass.SGST,
      sortable: true,
    },
    {
      name: t('CGST (%)'),
      selector: row => row && row.nestPass && row.nestPass.CGST,
      sortable: true,
    },
    {
      name: t('IGST (%)'),
      selector: row => row && row.nestPass && row.nestPass.IGST,
      sortable: true,
    },
    {
      name: t('Grace Period (Days)'),
      selector: row => row && row.nestPass && row.nestPass.gracePeriodForRenewal,
      sortable: true,
    },
    {
      name: t('Notification Before Expiry (Days)'),
      selector: row => row && row.nestPass && row.nestPass.notificationForExpiration,
      sortable: true,
    },*/
    {
      name: t('Is Active'),
      selector: (row) => (row && row.nestPass && row.nestPass.isActive ? t('Yes') : t('No')),
      sortable: true,
    },
    {
      name: t('Expiry In Months'),
      selector: (row) => row && row.nestPass && row.nestPass.expiryInMonths,
      sortable: true,
    },
    /*{
      name: t('Customer Message'),
      selector: row => row && row.nestPass && row.nestPass.message,
      sortable: true,
    },
    {
      name: t('Expiry Reminder Message'),
      selector: row => row && row.nestPass && row.nestPass.nestPassExpiryReminderPushNotificationMessage,
      sortable: true,
    },*/
    {
      name: t('Actions'),
      cell: (row) => (
        <Button onClick={() => handleOpenEditModal(row.nestPass)}>{t('Edit')}</Button>
        // You can add a delete button here as well, similar to ManageCoupons
      ),
    },
  ];

  const nestPassData = data && data.getStore && data.getStore.nestPass ? [data.getStore] : [];
  console.log('nestPassData before DataTable:', nestPassData);

  return (
    <>
      <Header />
      <Box sx={{ height: 16 }} />
      <Container>
        <Box sx={{ textAlign: 'right', mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAddModal}
            startIcon={<Icon icon="ic:twotone-plus" />}
          >
            {t('Add New Nest Pass')}
          </Button>
        </Box>

        {loading ? (
          <CustomLoader />
        ) : error ? (
          <Alert severity="error">
            {t('Error fetching data')}: {error.message}
          </Alert>
        ) : (
          <DataTable
            title={<TableHeader title={t('Manage Nest Pass')} />}
            columns={columns}
            data={nestPassData}
            pagination
            customStyles={customStyles} // Apply custom table styles
            progressPending={loading}
            progressComponent={<CustomLoader />}
            // sortFunction={customSort} // If you need custom sorting
          />
        )}
        {/* Nest Pass Notification Cron Configuration */}
        <Paper sx={{ mt: 4, p: 3, borderRadius: 1, color: 'black' }}>
          {/* Set default text color to black */}
          <Typography variant="h6" gutterBottom color="black">
            {t('Nest Pass Notification Cron Configuration')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {/* Keep items in a row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <Typography color="black">{t('Start Time')}:</Typography>
              <TextField
                type="time"
                value={cronStartTime}
                onChange={handleCronStartTimeChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min
                }}
                sx={{
                  width: 150,
                  '& input': {
                    // Target the input element inside TextField
                    color: 'black',
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <Typography color="black">{t('End Time')}:</Typography>
              <TextField
                type="time"
                value={cronEndTime}
                onChange={handleCronEndTimeChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min
                }}
                sx={{
                  width: 150,
                  '& input': {
                    // Target the input element inside TextField
                    color: 'black',
                  },
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateCronTimeSlot}
              disabled={updatingCron}
              sx={{ height: 56 }} // Match the height of the TextFields
            >
              {updatingCron ? t('Updating...') : t('Update Cron Time')}
            </Button>
            {updateCronError && (
              <Typography color="error" sx={{ ml: 2 }}>
                {t('Error updating cron')}: {updateCronError.message}
              </Typography>
            )}
          </Box>
          {data && data.getStore && data.getStore.nestPassExpiryNotificationSlot && (
            <Typography variant="body2" color="black">
              {' '}
              {/* Ensure this text is black */}
              {t('Current Cron Time Slot')}: {data.getStore.nestPassExpiryNotificationSlot.start} -{' '}
              {data.getStore.nestPassExpiryNotificationSlot.end}
            </Typography>
          )}
        </Paper>

        {/* Add Nest Pass Modal */}
        <Modal
          open={addModalOpen}
          onClose={handleCloseAddModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NestPassModel onClose={handleCloseAddModal} storeId={storeId} refetch={refetch} />
        </Modal>

        {/* Edit Nest Pass Modal */}
        <Modal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NestPassModel
            onClose={handleCloseEditModal}
            nestPass={selectedNestPass}
            storeId={storeId}
            refetch={refetch}
            isEdit={true}
          />
        </Modal>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default withTranslation()(ManageNestPass);
